import { useMemo } from "react";
import { Mail, PencilLine, Phone } from "lucide-react";
import StatusBadge from "./StatusBadge";

function normalizeText(value) {
  return String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
}

function normalizeEmail(value) {
  return normalizeText(value);
}

function normalizePhone(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const hasPlus = raw.startsWith("+") || raw.startsWith("00");
  let digits = raw.replace(/\D+/g, "");
  if (raw.startsWith("00") && digits.length > 2) {
    digits = digits.slice(2);
  }
  if (!digits) return "";
  return hasPlus ? `+${digits}` : digits;
}

function getSupplierMatch(contact, suppliers = []) {
  const email = normalizeEmail(contact?.email);
  const phone = normalizePhone(contact?.telefoon);
  const name = normalizeText(contact?.naam);

  return suppliers.find((supplier) => {
    const supplierName = normalizeText(supplier?.name);
    const supplierContact = normalizeText(supplier?.contactName);
    const supplierEmail = normalizeEmail(supplier?.email);
    const supplierPhone = normalizePhone(supplier?.phone);

    return Boolean(
      (email && supplierEmail && email === supplierEmail) ||
        (phone && supplierPhone && phone === supplierPhone) ||
        (name && (name === supplierContact || name === supplierName))
    );
  });
}

export default function ClientContactsTab({ contacts = [], suppliers = [], onEditContact }) {
  const supplierByContactId = useMemo(() => {
    const out = new Map();
    contacts.forEach((contact) => {
      const match = getSupplierMatch(contact, suppliers);
      if (match) {
        out.set(String(contact?.id || ""), match);
      }
    });
    return out;
  }, [contacts, suppliers]);

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">Contactpersonen</h3>
      </div>

      <div className="space-y-3">
        {contacts.map((contact) => {
          const supplierMatch = supplierByContactId.get(String(contact?.id || ""));
          const supplierActive = supplierMatch?.active !== false;

          return (
            <article key={contact.id} className="rounded-xl border border-slate-200 p-4">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h4 className="font-semibold text-slate-900">{contact.naam}</h4>
                  <p className="text-sm text-slate-500">{contact.functie}</p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge value={contact.type} />
                  {supplierMatch ? (
                    <span
                      className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${
                        supplierActive
                          ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                          : "border-slate-300 bg-slate-100 text-slate-600"
                      }`}
                      title={supplierMatch?.name ? `Gekoppeld aan leverancier: ${supplierMatch.name}` : "Gekoppeld aan leverancier"}
                    >
                      {supplierActive ? "Leverancier" : "Leverancier (inactief)"}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => onEditContact?.(contact)}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
                  >
                    <PencilLine size={14} /> Bewerken
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                <span className="inline-flex items-center gap-1"><Mail size={14} /> {contact.email}</span>
                <span className="inline-flex items-center gap-1"><Phone size={14} /> {contact.telefoon}</span>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
